/**
 * Shared sales vs bank reconciliation logic.
 * Used by SalesReconciliation UI and by notification creation (pos_mismatch).
 */

import { supabase } from '@/integrations/supabase/client';
import { platformDisplayName } from '@/lib/platformConstants';

export interface BankTxForReconciliation {
  id: string;
  transaction_date: string;
  amount: number;
  description: string;
}

export interface SalesTxForReconciliation {
  id: string;
  sale_date: string;
  order_date: string;
  payout_date: string;
  platform: string;
  location: string;
  net_deposit_amount: number;
  total_payout_amount: number;
  net_payout_amount: number;
}

export interface UnmatchedItem {
  related_entity_type: 'sales_transaction' | 'bank_transaction';
  related_entity_id: string;
  data_snippet: {
    platform?: string;
    payout_amount?: number;
    payout_date?: string;
    location?: string;
    bank_amount?: number;
    bank_date?: string;
    bank_description?: string;
  };
}

export const CONFIDENCE_THRESHOLD = 70;
export const DATE_TOLERANCE_DAYS = 7;
export const BIZ_DAYS_BEFORE = 1;
export const BIZ_DAYS_AFTER = 3;
const AMOUNT_TOLERANCE_MIN = 5;
const AMOUNT_TOLERANCE_MAX = 200;
const AMOUNT_TOLERANCE_PERCENT = 2;

/** Dynamic tolerance: between AMOUNT_TOLERANCE_MIN and AMOUNT_TOLERANCE_MAX, or 2% of amount. */
function amountTolerance(payoutAmount: number): number {
  if (payoutAmount <= 0) return AMOUNT_TOLERANCE_MIN;
  const byPercent = (payoutAmount * AMOUNT_TOLERANCE_PERCENT) / 100;
  return Math.min(AMOUNT_TOLERANCE_MAX, Math.max(AMOUNT_TOLERANCE_MIN, byPercent));
}

function isPlatformDeposit(tx: { description?: string | null; bucket?: string | null; category?: string | null; type?: string | null }): boolean {
  const desc = (tx.description || '').toLowerCase();
  const category = (tx.category || '').toLowerCase();

  const hasPlatformKeyword =
    desc.includes('doordash') || desc.includes('uber') || desc.includes('ubereats') || desc.includes('toast') ||
    desc.includes('citizens') || desc.includes('net setlmt') ||
    category.includes('doordash') || category.includes('uber') || category.includes('toast');

  return hasPlatformKeyword;
}

/**
 * Add N business days (Mon–Fri) to a date. Negative values go backwards.
 */
function addBusinessDays(date: Date, days: number): Date {
  const result = new Date(date);
  const direction = days >= 0 ? 1 : -1;
  let remaining = Math.abs(days);
  while (remaining > 0) {
    result.setDate(result.getDate() + direction);
    const dow = result.getDay();
    if (dow !== 0 && dow !== 6) remaining--;
  }
  return result;
}

/**
 * Compute the expected bank-deposit match window for a POS payout date.
 * Returns the earliest and latest calendar dates (inclusive) within which
 * a matching bank transaction should appear.
 */
export function getBusinessDayRange(
  payoutDate: Date | string,
  daysBefore: number = BIZ_DAYS_BEFORE,
  daysAfter: number = BIZ_DAYS_AFTER,
): { earliest: Date; latest: Date } {
  const base = typeof payoutDate === 'string' ? new Date(payoutDate + 'T12:00:00') : new Date(payoutDate);
  return {
    earliest: addBusinessDays(base, -daysBefore),
    latest: addBusinessDays(base, daysAfter),
  };
}

export interface ReconciliationRow {
  id: string;
  location: string;
  platform: string;
  payout_date: string;
  payout_amount: number;
  bank_deposit_amount: number;
  bank_deposit_date: string;
  due_amount: number;
  status: 'matched' | 'discrepancy' | 'pending' | 'unmatched';
  confidence_score?: number;
  bank_transaction_id?: string;
  sales_transaction_id?: string;
  bank_description?: string;
  is_bank_only?: boolean;
  /** Last date by which we expect a matching bank deposit to appear */
  expected_match_by?: string;
}

/**
 * Compute confidence score (0–100) for a match. Safe when payoutAmount is 0.
 */
function confidenceScore(
  amountDiff: number,
  daysDiff: number,
  payoutAmount: number
): number {
  if (payoutAmount <= 0) {
    return amountDiff < 0.01 ? 100 : 0;
  }
  const amountScore = Math.max(0, 100 - (amountDiff / payoutAmount) * 100);
  const daysScore = Math.max(0, 100 - (daysDiff / DATE_TOLERANCE_DAYS) * 100);
  return Math.round(amountScore * 0.7 + daysScore * 0.3);
}

/**
 * Run matching: sales vs bank. Returns reconciliation rows (sales + unmatched bank).
 * Uses a business-day window (1 biz day before, 3 biz days after payout date) for matching.
 * Unmatched payouts whose window hasn't expired yet are marked "pending".
 */
export function runReconciliation(
  bank: BankTxForReconciliation[],
  sales: SalesTxForReconciliation[],
  today: Date = new Date(),
): ReconciliationRow[] {
  const reconciled: ReconciliationRow[] = [];
  const matchedBankIds = new Set<string>();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  sales.forEach((sale) => {
    const payoutAmount = sale.net_payout_amount ?? sale.net_deposit_amount ?? sale.total_payout_amount ?? 0;
    const payoutDate = sale.payout_date || sale.sale_date || sale.order_date;
    const location = sale.location || 'Unknown';
    const tolerance = amountTolerance(payoutAmount);

    // Compute the business-day window for this payout
    const { earliest, latest } = getBusinessDayRange(payoutDate);
    const earliestMs = earliest.getTime();
    const latestMs = latest.getTime();

    let bestMatch: { bank: BankTxForReconciliation; diff: number; daysDiff: number } | null = null;

    bank.forEach((bankTx) => {
      if (matchedBankIds.has(bankTx.id)) return;

      const bankDateMs = new Date(bankTx.transaction_date + 'T12:00:00').getTime();

      // Primary: match within the business-day window
      const inWindow = bankDateMs >= earliestMs && bankDateMs <= latestMs;
      // Fallback: also allow the legacy wider tolerance for manual matching
      const daysDiff = Math.abs((bankDateMs - new Date(payoutDate + 'T12:00:00').getTime()) / 86400000);
      if (!inWindow && daysDiff > DATE_TOLERANCE_DAYS) return;

      const diff = Math.abs(bankTx.amount - payoutAmount);
      if (diff > tolerance) return;

      // Prefer in-window matches; among equals, prefer smaller amount diff then closer date
      if (!bestMatch) {
        bestMatch = { bank: bankTx, diff, daysDiff };
      } else {
        const prevInWindow = new Date(bestMatch.bank.transaction_date + 'T12:00:00').getTime() >= earliestMs
          && new Date(bestMatch.bank.transaction_date + 'T12:00:00').getTime() <= latestMs;
        if (inWindow && !prevInWindow) {
          bestMatch = { bank: bankTx, diff, daysDiff };
        } else if (inWindow === prevInWindow) {
          if (diff < bestMatch.diff || (diff === bestMatch.diff && daysDiff < bestMatch.daysDiff)) {
            bestMatch = { bank: bankTx, diff, daysDiff };
          }
        }
      }
    });

    const confidence = bestMatch
      ? confidenceScore(bestMatch.diff, bestMatch.daysDiff, payoutAmount)
      : 0;
    const matchedBank = bestMatch && confidence >= CONFIDENCE_THRESHOLD ? bestMatch.bank : null;
    if (matchedBank) matchedBankIds.add(matchedBank.id);

    let status: 'matched' | 'discrepancy' | 'pending' | 'unmatched' = 'unmatched';
    let bankAmount = 0;
    let bankDate = '';
    let dueAmount = payoutAmount;
    let bankDesc = '';

    if (matchedBank) {
      bankAmount = matchedBank.amount;
      bankDate = matchedBank.transaction_date;
      bankDesc = matchedBank.description || '';
      const diff = payoutAmount - bankAmount;
      status = Math.abs(diff) < 0.01 ? 'matched' : 'discrepancy';
      dueAmount = status === 'matched' ? 0 : diff;
    } else {
      // No match: determine pending vs unmatched based on whether the window has expired
      status = todayStart.getTime() <= latestMs ? 'pending' : 'unmatched';
    }

    const expectedMatchBy = latest.toISOString().split('T')[0];

    reconciled.push({
      id: sale.id,
      location,
      platform: sale.platform,
      payout_date: payoutDate,
      payout_amount: payoutAmount,
      bank_deposit_amount: bankAmount,
      bank_deposit_date: bankDate,
      due_amount: dueAmount,
      status,
      confidence_score: confidence,
      bank_transaction_id: matchedBank?.id,
      sales_transaction_id: sale.id,
      bank_description: bankDesc,
      is_bank_only: false,
      expected_match_by: expectedMatchBy,
    });
  });

  bank.forEach((bankTx) => {
    if (matchedBankIds.has(bankTx.id)) return;
    const desc = bankTx.description || '';
    const descLower = desc.toLowerCase();
    let platform = 'Unknown';
    if (descLower.includes('doordash')) platform = platformDisplayName('doordash');
    else if (descLower.includes('uber')) platform = platformDisplayName('uber');
    else if (descLower.includes('toast') || descLower.includes('citizens')) platform = platformDisplayName('toast');

    reconciled.push({
      id: bankTx.id,
      location: 'Unknown',
      platform,
      payout_date: bankTx.transaction_date,
      payout_amount: 0,
      bank_deposit_amount: bankTx.amount,
      bank_deposit_date: bankTx.transaction_date,
      due_amount: bankTx.amount,
      status: 'unmatched',
      confidence_score: 0,
      bank_transaction_id: bankTx.id,
      sales_transaction_id: undefined,
      bank_description: desc,
      is_bank_only: true,
    });
  });

  return reconciled;
}

/**
 * Runs reconciliation for a company and returns unmatched items only
 * (sales with no bank match, or bank deposits with no sales match).
 */
export async function getUnmatchedReconciliationItems(
  companyId: string,
  platformFilter?: string
): Promise<UnmatchedItem[]> {
  const unmatched: UnmatchedItem[] = [];

  const { data: bankData, error: bankError } = await supabase
    .from('bank_statements')
    .select('id, transaction_date, amount, description, bucket, category, type')
    .eq('company_id', companyId)
    .gt('amount', 0)
    .order('transaction_date', { ascending: false });

  if (bankError) throw bankError;

  const platformDeposits = (bankData || []).filter(isPlatformDeposit);
  const bank: BankTxForReconciliation[] = platformDeposits.map((tx) => ({
    id: tx.id,
    transaction_date: tx.transaction_date,
    amount: tx.amount,
    description: tx.description || '',
  }));

  let salesQuery = supabase
    .from('sales_transactions')
    .select('*')
    .eq('company_id', companyId)
    .not('payout_date', 'is', null)
    .order('payout_date', { ascending: false });

  if (platformFilter && platformFilter !== 'all') {
    salesQuery = salesQuery.ilike('platform', `%${platformFilter}%`);
  }

  const { data: salesData, error: salesError } = await salesQuery;
  if (salesError) throw salesError;
  const sales: SalesTxForReconciliation[] = (salesData || []) as SalesTxForReconciliation[];

  const matchedBankIds = new Set<string>();
  const today = new Date();
  const todayStart = new Date(today.getFullYear(), today.getMonth(), today.getDate());

  sales.forEach((sale) => {
    const payoutAmount = sale.net_payout_amount ?? sale.net_deposit_amount ?? sale.total_payout_amount ?? 0;
    const payoutDate = sale.payout_date || sale.sale_date || sale.order_date;
    const tolerance = amountTolerance(payoutAmount);
    const { earliest, latest } = getBusinessDayRange(payoutDate);
    const earliestMs = earliest.getTime();
    const latestMs = latest.getTime();

    let bestMatch: { bank: BankTxForReconciliation; diff: number; daysDiff: number } | null = null;

    bank.forEach((bankTx) => {
      if (matchedBankIds.has(bankTx.id)) return;
      const bankDateMs = new Date(bankTx.transaction_date + 'T12:00:00').getTime();
      const inWindow = bankDateMs >= earliestMs && bankDateMs <= latestMs;
      const daysDiff = Math.abs((bankDateMs - new Date(payoutDate + 'T12:00:00').getTime()) / 86400000);
      if (!inWindow && daysDiff > DATE_TOLERANCE_DAYS) return;
      const difference = Math.abs(bankTx.amount - payoutAmount);
      if (difference > tolerance) return;
      if (!bestMatch || difference < bestMatch.diff || (difference === bestMatch.diff && daysDiff < bestMatch.daysDiff)) {
        bestMatch = { bank: bankTx, diff: difference, daysDiff };
      }
    });

    const score = bestMatch ? confidenceScore(bestMatch.diff, bestMatch.daysDiff, payoutAmount) : 0;
    const matchedBank = bestMatch && score >= CONFIDENCE_THRESHOLD ? bestMatch.bank : null;
    if (matchedBank) matchedBankIds.add(matchedBank.id);

    // Only report truly unmatched items (past the pending window)
    if (!matchedBank && todayStart.getTime() > latestMs) {
      unmatched.push({
        related_entity_type: 'sales_transaction',
        related_entity_id: sale.id,
        data_snippet: {
          platform: sale.platform,
          payout_amount: payoutAmount,
          payout_date: payoutDate,
          location: sale.location || undefined,
        },
      });
    }
  });

  bank.forEach((bankTx) => {
    if (!matchedBankIds.has(bankTx.id)) {
      const desc = bankTx.description || '';
      const descLower = desc.toLowerCase();
      let platform: string | null = null;
      if (descLower.includes('doordash')) platform = 'DoorDash';
      else if (descLower.includes('uber')) platform = 'Uber Eats';
      else if (descLower.includes('toast') || descLower.includes('citizens')) platform = 'Toast';

      if (!platform) return;

      unmatched.push({
        related_entity_type: 'bank_transaction',
        related_entity_id: bankTx.id,
        data_snippet: {
          platform,
          bank_amount: bankTx.amount,
          bank_date: bankTx.transaction_date,
          bank_description: desc,
        },
      });
    }
  });

  return unmatched;
}
